/* eslint-disable jsx-a11y/anchor-is-valid */
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { ArticleCard } from '../ArticleCard/ArticleCard';
import { ArticleListInput } from './ArticlesList.types';
import { LinkButton } from '../LinkButton/LinkButton';
import { formatDateMonthYear } from '@/utils/date-formatting';

export const ArticlesList: React.FC<ArticleListInput> = ({
  title,
  showAs = 'cards',
  builderState,
  tagVariant = 'primary',
}) => {
  const { articles, currentPage = 1, totalPages = 1, urlPrefix } = builderState.state.articles || {};

  if (!articles || !articles.length) return null;

  const getPageNumbers = (): (number | 'dots')[] => {
    const pages: (number | 'dots')[] = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, 'dots', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, 'dots', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, 'dots', currentPage - 1, currentPage, currentPage + 1, 'dots', totalPages);
    }

    return pages;
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="my-12 flex flex-wrap items-center justify-center gap-10">
        <a
          data-testid="pagination-prev"
          href={currentPage > 1 ? `${urlPrefix}${currentPage === 2 ? '' : `/page/${currentPage - 1}`}` : '#'}
          className={`text-dark-100 hover:text-dark ${currentPage <= 1 ? 'pointer-events-none opacity-40' : ''}`}
          aria-disabled={currentPage <= 1}
        >
          <IconChevronLeft size={16} />
        </a>

        {getPageNumbers().map((item, idx) => {
          if (item === 'dots') {
            return (
              <span key={`dots-${idx}`} className="text-dark-100 px-2 font-semibold">
                ...
              </span>
            );
          }

          const isActive = item === currentPage;
          const isLastPage = item === totalPages;

          return (
            <a
              key={item}
              href={item !== 1 ? `${urlPrefix}/page/${item}` : `${urlPrefix}`}
              data-testid={isLastPage ? 'pagination-last-page' : `pagination-page-${item}`}
              className={`font-semibold ${isActive ? 'text-dark' : 'text-dark-100 hover:text-dark'}`}
            >
              {item}
            </a>
          );
        })}

        <a
          data-testid="pagination-next"
          href={currentPage < totalPages ? `${urlPrefix}/page/${currentPage + 1}` : '#'}
          className={`text-dark-100 hover:text-dark ${currentPage >= totalPages ? 'pointer-events-none opacity-40' : ''}`}
          aria-disabled={currentPage >= totalPages}
        >
          <IconChevronRight size={16} />
        </a>
      </div>
    );
  };

  return (
    <div data-testid="article-list">
      {title && <h2 data-testid="article-list-title">{title}</h2>}

      {showAs === 'cards' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((article) => (
            <ArticleCard key={article.url} article={article} tagVariant={tagVariant} />
          ))}
        </div>
      ) : (
        <ul className="pl-0">
          {articles.map((article) => (
            <li key={article.url} className="flex justify-between">
              <div>
                <h3 className="mb-1 text-xl font-bold">{article.title}</h3>
                {article.lastUpdated && <small>{formatDateMonthYear(new Date(article.lastUpdated))}</small>}
              </div>
              <div>
                <LinkButton link={article.url} label={'Go to article'} size={'medium'} variant={'tertiary'} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {renderPagination()}
    </div>
  );
};
